import type { QueryClient } from '@tanstack/react-query'
import type {
  BenefitQueryResultDto,
  PaginatedBenefitsResponse,
  UpdateBenefitRequestDto,
} from '../dtos/Benefit.dto'

/**
 * Patches matching rows in all cached `application-engine` query variants.
 *
 * Uses prefix-key matching so every filter/page combination in the cache is
 * updated — the same matching behaviour as `invalidateQueries`.
 *
 * Only benefits whose `id` appears in `patches` are touched; all other rows
 * are returned unchanged and nested relations (`persons`, `enrollment`,
 * `client`) are preserved via spread.
 *
 * Callers pass only the request fields (never the full API response) so
 * populated relations on the cached row are always preserved.
 *
 * @param queryClient - The React Query client instance
 * @param patches - Map of benefitId → fields to merge onto that row
 */
export function patchBenefitsInCache(
  queryClient: QueryClient,
  patches: Map<string, UpdateBenefitRequestDto>
): void {
  queryClient.setQueriesData<PaginatedBenefitsResponse>(
    { queryKey: ['application-engine'] },
    (oldData) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        data: oldData.data.map((benefit) => {
          const patch = patches.get(benefit.id)
          return patch
            ? ({ ...benefit, ...patch } as BenefitQueryResultDto)
            : benefit
        }),
      }
    }
  )
}
